/**
 * Client-side image compression — keep the upload well under the 6MB server
 * limit. Uses canvas to re-encode as JPEG at progressively lower quality
 * and (if needed) reduced max-dimension until size is acceptable.
 *
 * Returns a File (with a .jpg name) so the server sees a stable filename.
 */
const TARGET_MAX_BYTES = 4.5 * 1024 * 1024; // aim well below the 6MB hard cap
const MAX_DIMENSIONS = [2048, 1600, 1280, 1024, 800];
const QUALITIES = [0.9, 0.82, 0.72, 0.6];

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Could not read that image."));
        img.src = src;
    });
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(new Error("Could not read that image."));
        r.readAsDataURL(file);
    });
}

function drawScaled(img, maxDim) {
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    return canvas;
}

function canvasToBlob(canvas, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error("Encode failed"))),
            "image/jpeg",
            quality,
        );
    });
}

/**
 * @param {File} file
 * @returns {Promise<File>} JPEG file, ready for upload, comfortably < 6MB.
 */
export async function compressImage(file) {
    // Cheap fast-path: small enough already & is jpeg/png/webp → send as-is.
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (allowed.includes(file.type) && file.size <= TARGET_MAX_BYTES) {
        return file;
    }

    const dataUrl = await fileToDataUrl(file);
    const img = await loadImage(dataUrl);

    for (const maxDim of MAX_DIMENSIONS) {
        const canvas = drawScaled(img, maxDim);
        for (const q of QUALITIES) {
            const blob = await canvasToBlob(canvas, q);
            if (blob.size <= TARGET_MAX_BYTES) {
                return new File([blob], "cat-photo.jpg", { type: "image/jpeg" });
            }
        }
    }

    // Last resort — return whatever the smallest was; server will bounce if
    // still over the cap, and the UI shows the message.
    const smallCanvas = drawScaled(img, MAX_DIMENSIONS[MAX_DIMENSIONS.length - 1]);
    const smallBlob = await canvasToBlob(smallCanvas, QUALITIES[QUALITIES.length - 1]);
    return new File([smallBlob], "cat-photo.jpg", { type: "image/jpeg" });
}

export function isImageFile(file) {
    return file && /^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type);
}
